/**
 * Utility functions to serialize and deserialize TreeNode structures
 * for storage in sessionStorage/localStorage
 */

import { TreeNode } from "./TreeNode";

/**
 * Serialize a tree node structure by converting circular references
 * to ID references
 * @param {TreeNode[]} rootNodeList - Array of root TreeNodes
 * @param {TreeNode[]} nodeList - Array of all TreeNodes (BFS order)
 * @returns {Object} Serializable object
 */
export function serializeTreeNodes(rootNodeList, nodeList) {
  const serializedNodes = nodeList.map(node => ({
    id: node.id,
    value: node.value,
    level: node.level,
    descendantIds: node.descendants.map(d => d.id),
    parentId: node.parent ? node.parent.id : null
  }));

  const rootIds = rootNodeList.map(root => root.id);

  return {
    nodes: serializedNodes,
    rootIds: rootIds
  };
}

/**
 * Deserialize tree node structure from serialized format
 * @param {Object} serializedData - Serialized tree data
 * @returns {Object} { rootNodeList, nodeList }
 */
export function deserializeTreeNodes(serializedData) {
  if (!serializedData || !serializedData.nodes || !serializedData.rootIds) {
    return { rootNodeList: [], nodeList: [] };
  }

  const { nodes, rootIds } = serializedData;
  
  // Create TreeNode instances
  const nodeMap = new Map();
  const nodeList = nodes.map(serializedNode => {
    const node = new TreeNode(serializedNode.value);
    node.level = serializedNode.level;
    nodeMap.set(node.id, node);
    return node;
  });

  // Restore parent and descendants relationships
  nodes.forEach((serializedNode, index) => {
    const node = nodeList[index];
    
    // Restore parent
    if (serializedNode.parentId !== null) {
      node.parent = nodeMap.get(serializedNode.parentId);
    }
    
    // Restore descendants
    node.descendants = serializedNode.descendantIds
      .map(id => nodeMap.get(id))
      .filter(d => d !== undefined);
  });

  // Get root nodes
  const rootNodeList = rootIds
    .map(id => nodeMap.get(id))
    .filter(node => node !== undefined);

  return { rootNodeList, nodeList };
}

/**
 * Store serialized tree data in storage
 * @param {string} key - Storage key
 * @param {Object} data - Data to store
 * @param {boolean} useSessionStorage - Use sessionStorage (true) or localStorage (false)
 */
export function storeTreeData(key, data, useSessionStorage = true) {
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    storage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error storing tree data for key ${key}:`, error);
  }
}

/**
 * Retrieve serialized tree data from storage
 * @param {string} key - Storage key
 * @param {boolean} useSessionStorage - Use sessionStorage (true) or localStorage (false)
 * @returns {Object|null} Stored data or null
 */
export function retrieveTreeData(key, useSessionStorage = true) {
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    const data = storage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error retrieving tree data for key ${key}:`, error);
    return null;
  }
}

/**
 * Clear tree data from storage
 * @param {string} key - Storage key
 * @param {boolean} useSessionStorage - Use sessionStorage (true) or localStorage (false)
 */
export function clearTreeData(key, useSessionStorage = true) {
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    storage.removeItem(key);
  } catch (error) {
    console.error(`Error clearing tree data for key ${key}:`, error);
  }
}
